/**
 * Per-leg editor — one card per leg between consecutive waypoints. Each
 * card surfaces the leg's gimbal pose, recording state, and distance-
 * interval shooting config, with inline validation errors visible right
 * where they're caused (lens conflicts, interval distance ≤ 0, etc.).
 *
 * Mounted inside the Editor section of DockMissionDemoPanel, after the
 * waypoint list. Reads validation results from missionValidation.ts and
 * tags each card with its scoped errors.
 */

import { useDockDemoStore } from '../../stores/dockDemoStore';
import type { DemoMission, DemoLegParams, DemoPhotoPayload } from './types';
import { validateMission, type ValidationError } from '../../utils/missionValidation';
import './MissionLegEditor.css';

interface Props {
  mission: DemoMission;
}

const PAYLOADS: DemoPhotoPayload[] = ['wide', 'zoom', 'ir'];

/** Compact lens-set selector — multi-select via checkboxes. Used by both
 *  recording-lens (single value, but rendered as radios for visual
 *  consistency) and interval-photo-lenses (multi-select). */
function LensCheckboxes({
  selected,
  onChange,
}: {
  selected: DemoPhotoPayload[];
  onChange: (next: DemoPhotoPayload[]) => void;
}) {
  return (
    <div className="dock-demo-leg-lenses">
      {PAYLOADS.map((p) => {
        const isSel = selected.includes(p);
        return (
          <label key={p} className="dock-demo-leg-lenschip">
            <input
              type="checkbox"
              checked={isSel}
              onChange={(e) => {
                const next = e.target.checked
                  ? Array.from(new Set([...selected, p]))
                  : selected.filter((x) => x !== p);
                onChange(next);
              }}
            />
            {p}
          </label>
        );
      })}
    </div>
  );
}

/**
 * Snap a yaw input to nearest 5° when Shift is held during the change —
 * matches the map drag-handle's Shift-snap behaviour for consistency.
 */
function applyShiftSnap(value: number, shiftKey: boolean): number {
  if (!shiftKey) return value;
  return Math.round(value / 5) * 5;
}

export default function MissionLegEditor({ mission }: Props) {
  const updateWaypointLegParams = useDockDemoStore((s) => s.updateWaypointLegParams);
  const dockStations = useDockDemoStore((s) => s.dockStations);
  const boundDock = mission.dockStationId
    ? dockStations.find((d) => d.id === mission.dockStationId) ?? null
    : null;
  const wps = mission.waypoints;
  const errors: ValidationError[] = validateMission(mission, boundDock);

  // No legs to edit if there are < 2 waypoints.
  if (wps.length < 2) {
    return (
      <div className="dock-demo-leg-empty">
        <p>Add at least two waypoints to configure leg parameters.</p>
        <p className="dock-demo-hint">
          Each leg lets you set gimbal pointing, video recording, and distance-interval
          photos for the segment between two waypoints.
        </p>
      </div>
    );
  }

  const setLeg = (waypointId: string, patch: Partial<DemoLegParams>) => {
    updateWaypointLegParams(mission.id, waypointId, patch);
  };

  return (
    <div className="dock-demo-leg-cards">
      {wps.slice(0, -1).map((wp, i) => {
        const lp = wp.legParams ?? {};
        const next = wps[i + 1];
        const legErrors = errors.filter(
          (e) => e.scope.kind === 'leg' && e.scope.legIndex === i,
        );
        const interval = lp.intervalPhoto ?? { enabled: false, distanceM: 5, lenses: ['wide', 'ir'] as DemoPhotoPayload[] };

        return (
          <div
            key={wp.id}
            className={`dock-demo-leg-card ${legErrors.length > 0 ? 'dock-demo-leg-card--error' : ''}`}
          >
            <div className="dock-demo-leg-card__head">
              <span className="dock-demo-leg-card__title">
                Leg {i + 1} → {i + 2}
              </span>
              <span className="dock-demo-leg-card__sub">
                from WP{i + 1} to WP{i + 2}
              </span>
            </div>

            <div className="dock-demo-leg-card__row">
              <label className="dock-demo-leg-field" title="0=N · 90=E · 180=S · 270=W. Shift+arrow snaps to 5°.">
                <span>Gimbal yaw (°)</span>
                <input
                  type="number"
                  min={0}
                  max={360}
                  step={1}
                  placeholder="carry over"
                  value={lp.gimbalYaw ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return setLeg(wp.id, { gimbalYaw: undefined });
                    const n = applyShiftSnap(Number(raw), e.nativeEvent instanceof KeyboardEvent && e.nativeEvent.shiftKey);
                    setLeg(wp.id, { gimbalYaw: ((n % 360) + 360) % 360 });
                  }}
                />
              </label>

              <label className="dock-demo-leg-field" title="-90 = straight down · 0 = horizon · +30 = up 30°">
                <span>Gimbal pitch (°)</span>
                <input
                  type="number"
                  min={-90}
                  max={30}
                  step={1}
                  placeholder="carry over"
                  value={lp.gimbalPitch ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return setLeg(wp.id, { gimbalPitch: undefined });
                    setLeg(wp.id, { gimbalPitch: Number(raw) });
                  }}
                />
              </label>
            </div>

            <div className="dock-demo-leg-card__row">
              <label className="dock-demo-leg-field dock-demo-leg-field--toggle">
                <input
                  type="checkbox"
                  checked={!!lp.recordVideo}
                  onChange={(e) =>
                    setLeg(wp.id, {
                      recordVideo: e.target.checked,
                      // Default to zoom (tele RGB) — the only M3T config that
                      // frees both wide + ir for paired interval photos.
                      recordVideoLens: e.target.checked ? (lp.recordVideoLens ?? 'zoom') : lp.recordVideoLens,
                    })
                  }
                />
                <span>Record video</span>
              </label>

              {lp.recordVideo && (
                <label className="dock-demo-leg-field dock-demo-leg-field--inline">
                  <span>Video lens</span>
                  <select
                    value={lp.recordVideoLens ?? 'zoom'}
                    onChange={(e) =>
                      setLeg(wp.id, { recordVideoLens: e.target.value as DemoPhotoPayload })
                    }
                  >
                    {PAYLOADS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div className="dock-demo-leg-card__row dock-demo-leg-card__row--col">
              <label className="dock-demo-leg-field dock-demo-leg-field--toggle">
                <input
                  type="checkbox"
                  checked={interval.enabled}
                  onChange={(e) =>
                    setLeg(wp.id, {
                      intervalPhoto: { ...interval, enabled: e.target.checked },
                    })
                  }
                />
                <span>Interval photo</span>
              </label>

              {interval.enabled && (
                <div className="dock-demo-leg-card__row">
                  <label className="dock-demo-leg-field dock-demo-leg-field--inline">
                    <span>every (m)</span>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      step={1}
                      value={interval.distanceM}
                      onChange={(e) =>
                        setLeg(wp.id, {
                          intervalPhoto: {
                            ...interval,
                            distanceM: Number(e.target.value) || 0,
                          },
                        })
                      }
                    />
                  </label>
                  <div className="dock-demo-leg-field">
                    <span>Lenses</span>
                    <LensCheckboxes
                      selected={interval.lenses}
                      onChange={(next) =>
                        setLeg(wp.id, {
                          intervalPhoto: { ...interval, lenses: next },
                        })
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            {legErrors.length > 0 && (
              <ul className="dock-demo-leg-errors">
                {legErrors.map((e, idx) => (
                  <li key={`${e.code}-${idx}`}>{e.message}</li>
                ))}
              </ul>
            )}

            <div className="dock-demo-leg-card__foot">
              <span className="dock-demo-hint">
                Distance ≈ {(distanceM(wp.lng, wp.lat, next.lng, next.lat)).toFixed(1)} m
              </span>
              {(lp.gimbalYaw === undefined && lp.gimbalPitch === undefined && lp.recordVideo === undefined && !lp.intervalPhoto) && (
                <span className="dock-demo-hint dock-demo-hint--soft">
                  No params set — leg inherits previous state.
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Cheap-and-cheerful spherical distance for the per-leg "≈ N m" label.
 *  Lifted from liveViewSimulator's haversine; duplicated here so the panel
 *  doesn't pull in the simulator just for one display string. */
function distanceM(lng1: number, lat1: number, lng2: number, lat2: number): number {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const dφ = ((lat2 - lat1) * Math.PI) / 180;
  const dλ = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
